import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation } from 'react-router';

import { AuthCard, AuthHead, AuthShell } from '../_shared/AuthShell';
import {
  ActionRow,
  AuthButton,
  AuthFoot,
  Field,
  FormError,
  FormStatus,
  RequiredNote,
  useOnline,
} from '../_shared/components';
import { AUTH_ROUTES, OTP_USAGE } from '../_shared/constants';
import { toFieldFailure, toFormFailure, type FormFailure } from '../_shared/failure';
import { useAuthConfiguration, useRecoverPassword, useRequestOtp } from '../_shared/queries';
import type { AuthHandoff } from '../_shared/types';

import { copy } from './copy';
import {
  buildRecoverSchema,
  recoverIdentitySchema,
  type RecoverFields,
  type RecoverIdentityFields,
} from './forms.schema';

/**
 * Password recovery — the one route in this console whose submit is
 * disabled until the form is valid, because the catalog attaches that behaviour
 * to this screen specifically.
 *
 * **Validity is computed over every required control**, not over the password
 * pair alone: the screen has a code step in front of it, and gating on the two
 * passwords would enable the save before a code had been entered.
 *
 * The disabled state is never the only feedback. Each field carries its own
 * per-state error, and the static line beside the button says what the
 * condition is — a disabled control with no written reason is a defect.
 */
export function RecoverPage() {
  const location = useLocation();
  const online = useOnline();

  const handoff = location.state as Partial<AuthHandoff> | null;
  const [phone, setPhone] = useState(handoff?.phone ?? '');
  // True only when the number was collected on this screen. That operator was
  // never told a message had been sent, so the form says so; the one handed a
  // number by the sign-in screen already read that promise in the lede.
  const [identifiedHere, setIdentifiedHere] = useState(false);

  const [failure, setFailure] = useState<FormFailure | null>(null);
  const [saved, setSaved] = useState(false);

  const configuration = useAuthConfiguration();
  const requestOtp = useRequestOtp();
  const recoverPassword = useRecoverPassword();

  const schema = useMemo(
    () => buildRecoverSchema(configuration.data?.otp_code_length),
    [configuration.data?.otp_code_length],
  );

  const form = useForm<RecoverFields>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: { code: '', password: '', passwordConfirm: '' },
  });

  useEffect(() => {
    document.title = copy.documentTitle;
  }, []);

  /**
   * The lede promises a code «يصلك … على رقم موبايلك», so arriving on the
   * screen is what sends it — a recovery code is a different usage from a
   * sign-in code and the sign-in screen never issued one.
   *
   * The ref is not a style preference: React mounts an effect twice in
   * development, and a second dispatch would bill an SMS, trip the per-number
   * cap, and hand the operator a `429` for a screen they had only opened.
   */
  const dispatched = useRef(false);
  const requestCode = requestOtp.mutate;

  useEffect(() => {
    if (!phone || dispatched.current) return;
    dispatched.current = true;

    requestCode(
      { phone, usage_type: OTP_USAGE.recovery },
      {
        // A refusal here is worth stating but not worth blocking on: the
        // operator may already hold a live code from a previous attempt, and
        // the field below still accepts it.
        onError: (error) => setFailure(toFormFailure(error)),
      },
    );
  }, [phone, requestCode]);

  /**
   * The identity step, and why it exists.
   *
   * The sign-in screen hands this screen a number in location state, and it hands
   * over an **empty** one whenever the link is pressed before the field is filled —
   * which is the ordinary way the link is used, because the operator who has
   * forgotten their password has not typed anything yet. A typed `/recover` address
   * arrives the same way. Both used to be answered with a silent redirect back to
   * `/login`, so the route rendered for nobody and the only visible symptom was a
   * link that appeared to do nothing.
   *
   * Asking for the number is not a fifth field on the recovery form: it is the step
   * in front of it, drawn only when the screen was not told who is recovering, and
   * it disappears the moment the code is on its way.
   */
  if (!phone) {
    return (
      <IdentityStep
        onIdentified={(value) => {
          setFailure(null);
          setIdentifiedHere(true);
          setPhone(value);
        }}
      />
    );
  }

  async function onSubmit(values: RecoverFields) {
    setFailure(null);

    try {
      await recoverPassword.mutateAsync({
        phone,
        code: values.code,
        password: values.password,
        password_repeat: values.passwordConfirm,
      });

      setSaved(true);
      form.reset({ code: '', password: '', passwordConfirm: '' });
    } catch (error) {
      const fieldFailure = toFieldFailure(error);
      if (fieldFailure) {
        form.setError(fieldFailure.field as keyof RecoverFields, { message: fieldFailure.message });
      }
      setFailure(toFormFailure(error));
    }
  }

  const pending = recoverPassword.isPending;

  return (
    <AuthShell>
      <AuthHead title={copy.head.title}>{copy.head.lede}</AuthHead>

      {/*
        Mounted rather than conditional: a live region revealed and populated in
        one frame is announced unreliably.
      */}
      <FormStatus message={saved ? copy.saved : identifiedHere ? copy.phone.sent : null} />

      {failure ? <FormError message={failure.message} tone={failure.tone} /> : null}

      <AuthCard>
        <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
          <RequiredNote />

          <Field
            {...form.register('code')}
            id="r-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            dir="ltr"
            codeInput
            label={copy.code.label}
            requiredMark
            hint={copy.code.hint}
            {...(form.formState.errors.code?.message ? { error: form.formState.errors.code.message } : {})}
          />

          <Field
            {...form.register('password')}
            id="r-pass"
            type="password"
            revealable
            autoComplete="new-password"
            dir="ltr"
            label={copy.password.label}
            requiredMark
            hint={copy.password.hint}
            {...(form.formState.errors.password?.message
              ? { error: form.formState.errors.password.message }
              : {})}
          />

          {/*
            The match is checked when the confirmation field is left, and only
            then while typing — RHF's `onTouched` mode is that rule exactly. The
            error appears under the field and does not replace its hint.
          */}
          <Field
            {...form.register('passwordConfirm')}
            id="r-pass2"
            type="password"
            revealable
            autoComplete="new-password"
            dir="ltr"
            label={copy.passwordConfirm.label}
            requiredMark
            hint={copy.passwordConfirm.hint}
            {...(form.formState.errors.passwordConfirm?.message
              ? { error: form.formState.errors.passwordConfirm.message }
              : {})}
          />

          <ActionRow>
            <AuthButton type="submit" size="lg" disabled={!form.formState.isValid || pending}>
              {online ? copy.actions.save : copy.actions.offline}
            </AuthButton>
          </ActionRow>

          <p className="mb-0 mt-3 max-w-none text-xs text-ink-muted">{copy.note}</p>
        </form>
      </AuthCard>

      <AuthFoot>
        <Link to={AUTH_ROUTES.login}>{copy.foot.remembered}</Link>
      </AuthFoot>
    </AuthShell>
  );
}

/**
 * «من هو صاحب الحساب؟» — the step in front of the recovery form.
 *
 * Drawn only when the screen was reached without a number, and it exists because
 * both ways of reaching it that way are ordinary: pressing «نسيت كلمة المرور؟»
 * on an untouched sign-in form, and typing the address. Neither may dead-end,
 * and the previous answer — a silent `<Navigate>` back to `/login` — made the
 * route unreachable through its own entry point.
 *
 * The number is only handed upward; this component sends no code itself. The
 * page's existing dispatch effect fires the moment a number exists, so there is
 * one request path rather than two that can disagree about how many messages an
 * operator is billed for.
 */
function IdentityStep({ onIdentified }: { onIdentified: (phone: string) => void }) {
  const online = useOnline();

  const form = useForm<RecoverIdentityFields>({
    resolver: zodResolver(recoverIdentitySchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: { phone: '' },
  });

  useEffect(() => {
    document.title = copy.documentTitle;
  }, []);

  return (
    <AuthShell>
      <AuthHead title={copy.head.title}>{copy.head.lede}</AuthHead>

      <AuthCard>
        <form
          noValidate
          onSubmit={form.handleSubmit((values) => {
            onIdentified(values.phone);
          })}
        >
          <RequiredNote />

          <Field
            {...form.register('phone')}
            id="r-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            dir="ltr"
            label={copy.phone.label}
            requiredMark
            hint={copy.phone.hint}
            {...(form.formState.errors.phone?.message ? { error: form.formState.errors.phone.message } : {})}
          />

          <ActionRow>
            {/*
              Not disabled-until-valid. That behaviour belongs to the recovery
              form itself — the catalog attaches it to exactly one control — and a
              second disabled button in front of it would gate the screen before
              the operator had been told what is wrong with what they typed.
            */}
            <AuthButton type="submit" size="lg">
              {online ? copy.phone.submit : copy.actions.offline}
            </AuthButton>
          </ActionRow>
        </form>
      </AuthCard>

      <AuthFoot>
        <Link to={AUTH_ROUTES.login}>{copy.foot.remembered}</Link>
      </AuthFoot>
    </AuthShell>
  );
}
